import { paginateNdtGlobalItems, type NdtGlobalListQuery, type NdtGlobalListResult } from "../../domain/ndt/ndt-global-list-query.js";
import type { RequestContext } from "../context/request-context.js";
import type { PmConsumableRepository } from "../../repositories/operational-repositories.js";
import {
  type PmConsumable,
  type PmConsumableFilters,
  type PmConsumableStatus,
  type PmConsumableType,
  applyPmConsumableFilters,
  selectablePmConsumables,
  validatePmConsumable
} from "../../domain/ndt/pm-consumable.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Diz se um consumível PM já está apontado por algum relatório — vinculado nunca é apagado, só inativado. */
export type PmConsumableUsageChecker = (consumableId: string) => Promise<boolean>;

/**
 * Cadastro global END → Consumíveis PM. Mesmas permissões do restante do
 * cadastro de END (NdtRegistry*). Preparado para o Relatório PM: o checker de
 * uso entra aqui quando esse relatório passar a apontar consumíveis.
 */
export class PmConsumableService {
  constructor(
    private readonly repo: PmConsumableRepository,
    private readonly isInUse?: PmConsumableUsageChecker
  ) {}

  /** M21: limite 100 no backend, busca global sem escopo de Cliente. */
  async listPaged(context: RequestContext, input: NdtGlobalListQuery): Promise<NdtGlobalListResult<PmConsumable>> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const repository = this.repo as unknown as {
      listPaged?: (query: NdtGlobalListQuery) => Promise<NdtGlobalListResult<PmConsumable>>;
    };
    if (repository.listPaged) return repository.listPaged(input);
    return paginateNdtGlobalItems(await this.repo.listAll(), input);
  }

  async list(context: RequestContext, filters: PmConsumableFilters = {}): Promise<PmConsumable[]> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    return applyPmConsumableFilters(all, filters).sort((a, b) =>
      a.type.localeCompare(b.type) || a.brand.localeCompare(b.brand, "pt-BR") || a.product.localeCompare(b.product, "pt-BR")
    );
  }

  async getById(context: RequestContext, id: string): Promise<PmConsumable | null> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    return this.repo.findById(id);
  }

  /** O que o Relatório PM oferece num select: ATIVOS do tipo, mais o já vinculado. */
  async listSelectableForReport(context: RequestContext, type: PmConsumableType, currentlyLinkedId?: string | null): Promise<PmConsumable[]> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    return selectablePmConsumables(all, type, currentlyLinkedId);
  }

  async create(context: RequestContext, data: Partial<PmConsumable>): Promise<PmConsumable> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryCreate);

    const errors = validatePmConsumable(data);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid PM consumable: ${errors.join(" ")}`, { safeMessage: errors.join(" ") });
    }
    await this.assertLotIsUnique(data.type as PmConsumableType, data.brand!.trim(), data.product!.trim(), data.lot!.trim(), null);

    const now = new Date().toISOString();
    const item: PmConsumable = {
      id: `PMC-${randomUUID()}`,
      type: data.type as PmConsumableType,
      brand: data.brand!.trim(),
      product: data.product!.trim(),
      lot: data.lot!.trim(),
      manufacturingDate: (data.manufacturingDate || "").trim(),
      validity: data.validity!.trim(),
      technique: (data.technique || "") as PmConsumable["technique"],
      status: (data.status as PmConsumableStatus) || "ACTIVE",
      observations: data.observations || "",
      createdAt: now,
      updatedAt: now,
      createdBy: context.userId || "system",
      updatedBy: context.userId || "system"
    };
    await this.repo.save(item);
    return item;
  }

  async update(context: RequestContext, id: string, data: Partial<PmConsumable>): Promise<PmConsumable> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryUpdate);
    const existing = await this.requireItem(id);
    const merged: PmConsumable = { ...existing, ...data, id: existing.id, createdAt: existing.createdAt, createdBy: existing.createdBy };

    const errors = validatePmConsumable(merged);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid PM consumable: ${errors.join(" ")}`, { safeMessage: errors.join(" ") });
    }
    await this.assertLotIsUnique(merged.type, merged.brand.trim(), merged.product.trim(), merged.lot.trim(), existing.id);

    const updated: PmConsumable = {
      ...merged,
      brand: merged.brand.trim(),
      product: merged.product.trim(),
      lot: merged.lot.trim(),
      manufacturingDate: (merged.manufacturingDate || "").trim(),
      validity: merged.validity.trim(),
      technique: (merged.technique || "") as PmConsumable["technique"],
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };
    await this.repo.save(updated);
    return updated;
  }

  async setStatus(context: RequestContext, id: string, status: PmConsumableStatus): Promise<PmConsumable> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryInactivate);
    const existing = await this.requireItem(id);
    const updated: PmConsumable = { ...existing, status, updatedAt: new Date().toISOString(), updatedBy: context.userId || "system" };
    await this.repo.save(updated);
    return updated;
  }

  /** Nunca utilizado: exclui. Já utilizado num relatório/histórico: só inativa. */
  async delete(context: RequestContext, id: string): Promise<void> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryDelete);
    const existing = await this.requireItem(id);
    if (this.isInUse && await this.isInUse(existing.id)) {
      throw new AppError(ErrorCode.Conflict, "PM consumable is referenced by a report", {
        safeMessage: `O consumível "${existing.brand} — ${existing.product} (Lote ${existing.lot})" já está vinculado a um relatório e não pode ser excluído. Utilize a ação de inativar.`
      });
    }
    await this.repo.delete(existing.id);
  }

  private async requireItem(id: string): Promise<PmConsumable> {
    const existing = await this.repo.findById(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "PM consumable not found", { safeMessage: "Consumível PM não encontrado." });
    }
    return existing;
  }

  private async assertLotIsUnique(type: PmConsumableType, brand: string, product: string, lot: string, ignoreId: string | null): Promise<void> {
    const all = await this.repo.listAll();
    const duplicate = all.find(i =>
      i.id !== ignoreId && i.type === type &&
      i.brand.trim().toLowerCase() === brand.toLowerCase() &&
      i.product.trim().toLowerCase() === product.toLowerCase() &&
      i.lot.trim().toLowerCase() === lot.toLowerCase()
    );
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, "Duplicated PM consumable lot", {
        safeMessage: `Já existe o lote "${lot}" cadastrado para ${brand} — ${product}.`
      });
    }
  }
}
